/* @flow */
import { getterFromKeyPath, alphabetize, capitalize } from 'trustar/utils';
import { createSelector } from 'reselect';
import { OrderedMap, Map } from 'immutable';
import isString from 'lodash.isstring';
import escapeRegExp from 'lodash.escaperegexp';
import { _Enclave as EnclaveRecord } from 'modules/enclave/types.js';
import { templatesMap as templates } from '../templates/getters';

export const colors = getterFromKeyPath(['enclaveManagement', 'colors']);
export const loading = getterFromKeyPath(['enclaveManagement', 'loading']);
export const loaded = getterFromKeyPath(['enclaveManagement', 'loaded']);
export const displayOptions = getterFromKeyPath(['enclaveManagement', 'options']);

// Enclave Query Results, to be used in searched filters without altering original map
export const enclavesQueryResult = getterFromKeyPath(['enclaveManagement', 'enclavesQueryResult']);
export const queriedEnclavesLoading = getterFromKeyPath(['enclaveManagement', 'queriedEnclavesLoading']);

/**
 * The primary enclave getter. This is computed rather than just a keypath so
 * that we can merge in persistent colors. This was part of the feature of
 * persisting colors so that they do not get reset on every browser refresh.
 *
 * NOTE: In the future this color should be persisted in the database so that
 * all users who have access to an enclave see the same color, as well as
 * sessions between browsers. It would also allows us to remove this bit of
 * complexity and return to a nice plain keypath getter.
 */
const _enclaves = getterFromKeyPath(['enclaveManagement', 'enclaves']);

export const enclave = (id: number) => {
  return createSelector(
    enclavesMap,
    (enclaves: OrderedMap<any, any>) => enclaves.get(id),
  );
};

export const enclavesMap = createSelector(
  [ _enclaves, colors ],
  (enclaves: OrderedMap<any, any>, colors: any) => {
    enclaves = enclaves || OrderedMap();
    return enclaves.map(x => x.set('color', colors.get(x.id) || x.color))
      .sort(alphabetize);
  }
);

export const queriedEnclavesMap = createSelector(
  [ enclavesQueryResult, colors ],
  (enclaves: OrderedMap<any, any>, colors: any) => {
    enclaves = enclaves || OrderedMap();
    return enclaves.map(x => x.set('color', colors.get(x.id) || x.color))
      .sort(alphabetize);
  }
);

// This filtered map only uses a local filter
export const filteredEnclavesMap = createSelector(
  [enclavesMap, displayOptions],
  (enclavesMap: OrderedMap<any, any>, options: OrderedMap<any, any>) => {
    return enclavesMap.filter(enclave => {
        const query = options.get('query');
        const regex = new RegExp(escapeRegExp(query), 'i');

        // Test all top-level string values
        return enclave
          .toSeq()
          .valueSeq()
          .filter(isString)
          .some(x => x.search(regex) > -1);
      })

  }
)

export const enclaves = createSelector(
  enclavesMap,
  (enclaves: OrderedMap<any, any>) => enclaves.toList()
);

export const queriedEnclaves = createSelector(
  queriedEnclavesMap,
  (enclaves: OrderedMap<any, any>) => enclaves.toList().map(enclave => ({
    value: enclave.get('id'), //
    label: capitalize(enclave.get('name')),
  }))
);

/*
 * Generate initial form values for adding/editing enclave.
 */
export const getInitialFormValues = (enclave: ?Map<any, any>) => createSelector(
  templates,
  (templates: Map<any, any>) => {
    return !enclave
      ? Map(EnclaveRecord({}).toObject())
      : enclave;
  });



// WEBPACK FOOTER //
// ./src/app/modules/enclave-management/getters.js